<template>
  <FormField :name="name" v-slot="{ componentField }">
    <FormItem :class="props.class">
      <FormLabel>Project</FormLabel>
      <Select v-bind="componentField">
        <FormControl>
          <SelectTrigger :disabled="projects && projects?.length === 0">
            <SelectValue :placeholder="isLoading ? 'Loading...' : 'Select a project'">
              <div v-if="isLoading" class="flex items-center gap-2">
                <Spinner />
                <span>Loading...</span>
              </div>
              <div v-else-if="selected" class="flex items-center gap-2">
                <ProjectIcon
                  :color="selected?.color"
                  width="26"
                  height="26"
                  :name="selected?.name"
                />
                {{ selected?.name }}
              </div>
              <span v-else>Select a project</span>
            </SelectValue>
          </SelectTrigger>
        </FormControl>
        <SelectContent :position="'popper'">
          <SelectGroup>
            <SelectItem v-for="project in projects" :key="project.uuid" :value="project.uuid">
              <div class="flex items-center gap-2">
                <ProjectIcon :color="project.color" width="26" height="26" :name="project.name" />
                {{ project.name }}
              </div>
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      <FormMessage />
    </FormItem>
  </FormField>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { useField } from 'vee-validate';
import type { HTMLAttributes } from 'vue';
import { computed, watchEffect } from 'vue';

import { projectsQueryOptions } from '@/entities/project';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/form/new';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Spinner } from '@/shared/ui/spinner';

import ProjectIcon from './project-icon.vue';

const props = withDefaults(
  defineProps<{
    name?: string;
    modelValue?: string;
    class?: HTMLAttributes['class'];
  }>(),
  {
    name: 'project',
  },
);

const { data: projects, isLoading } = useQuery(projectsQueryOptions());

const field = useField<string>(props.name);

const selected = computed(() => {
  return projects.value?.find((p) => p.uuid === field.value.value);
});

watchEffect(() => {
  if (!field.value.value && projects.value) {
    field.resetField({ value: projects.value.find((p) => p.is_default)?.uuid });
  }
});
</script>
